import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { apiRequest } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import '../admin/quotes/quote-generator.css';

type InspectionRow = {
    _id: string;
    siteAddress?: string;
    frequency?: 'monthly' | 'quarterly' | 'semi_annual' | 'annual' | 'biennial' | string;
    standardCode?: 'NFPA_72' | 'ULC_S536' | string;
    status: 'in_progress' | 'completed';
    createdAt?: string;
    clientId?: { name?: string; firstName?: string; lastName?: string; buildingName?: string } | null;
    technicianId?: { name?: string } | null;
};

function clientName(row: InspectionRow): string {
    const c = row.clientId;
    if (!c) return '—';
    const n = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return n || c.buildingName || '—';
}

function formatFrequency(value?: string): string {
    if (!value) return '—';
    return value.replace('_', '-').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatStandard(value?: string): string {
    if (value === 'ULC_S536') return 'CAN/ULC-S536:2019';
    if (value === 'NFPA_72') return 'NFPA 72';
    return value || '—';
}

export default function InspectionsList(): React.JSX.Element {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const role = ((user as any)?.role || '') as string;
    const canManage = role === 'admin' || role === 'manager';
    const basePath = canManage ? '/admin/inspections' : '/technician/inspections';

    const [inspections, setInspections] = useState<InspectionRow[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const load = useCallback(() => {
        apiRequest<{ data: InspectionRow[] }>('/api/inspections')
            .then((res) => {
                setErrorMessage(null);
                setInspections(Array.isArray(res?.data) ? res.data : []);
            })
            .catch((err: unknown) => {
                setErrorMessage(err instanceof Error ? err.message : 'Failed to load inspections.');
                setInspections([]);
            });
    }, []);

    useEffect(() => {
        load();
    }, [load, location.key]);

    function openInspectionRow(insp: InspectionRow) {
        if (insp.status === 'completed') {
            navigate(`${basePath}/${insp._id}/summary`);
            return;
        }
        if (canManage) {
            navigate(`/admin/inspections/${insp._id}/field`);
            return;
        }
        navigate(`${basePath}/${insp._id}/checklist`);
    }

    function statusBadge(status: string) {
        const color = status === 'completed' ? 'var(--dash-green, #2e8a57)' : '#e6a817';
        return (
            <span style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: 999,
                fontSize: '0.78rem',
                fontWeight: 700,
                background: status === 'completed' ? 'rgba(46,138,87,0.1)' : 'rgba(230,168,23,0.12)',
                color
            }}>
                {status === 'in_progress' ? 'In Progress' : 'Completed'}
            </span>
        );
    }

    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container" style={{ maxWidth: '1100px' }}>
                    {errorMessage && (
                        <div className="banner banner-error" style={{ marginBottom: '16px' }}>
                            {errorMessage}
                        </div>
                    )}

                    <header className="quote-header" style={{ marginBottom: '28px' }}>
                        <div>
                            <h1 style={{ fontSize: '2.5rem', marginBottom: '8px' }}>Inspections</h1>
                            <p style={{ color: 'var(--dash-muted)', margin: 0, fontSize: '1.05rem' }}>
                                {canManage
                                    ? 'Track inspections and review completed reports. Field technicians perform checklists in their portal.'
                                    : 'Inspections assigned to you. On the checklist page, open the Building devices tab to view or add registered assets for that site.'}
                            </p>
                        </div>
                        {canManage && (
                            <button
                                type="button"
                                className="btn-primary"
                                style={{ padding: '12px 24px', fontSize: '0.95rem', boxShadow: '0 4px 14px rgba(46,138,87,0.3)' }}
                                onClick={() => navigate('/admin/work-order/new?jobType=inspection')}
                            >
                                + New Inspection
                            </button>
                        )}
                    </header>

                    <section>
                        <div className="quote-section-title" style={{ border: 'none', fontSize: '1.2rem', marginBottom: '14px' }}>
                            All Inspections ({inspections.length})
                        </div>
                        <div className="quote-card" style={{ padding: 0, overflow: 'hidden' }}>
                            {inspections.length === 0 ? (
                                <div style={{ padding: '36px 24px', textAlign: 'center', color: 'var(--dash-muted)' }}>
                                    <p style={{ margin: 0, fontSize: '1rem' }}>No inspections yet.</p>
                                    {canManage && (
                                        <button
                                            type="button"
                                            className="btn-primary"
                                            style={{ marginTop: '16px' }}
                                            onClick={() => navigate('/admin/work-order/new?jobType=inspection')}
                                        >
                                            Create first inspection
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="list-responsive">
                                    <div className="list-table-pane quote-table-container">
                                        <table className="invoice-table">
                                            <thead>
                                                <tr>
                                                    <th>Client</th>
                                                    <th>Site Address</th>
                                                    {canManage && <th>Technician</th>}
                                                    <th>Inspection Type</th>
                                                    <th>Standard</th>
                                                    <th>Date</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {inspections.map((insp) => (
                                                    <tr
                                                        key={insp._id}
                                                        role="button"
                                                        tabIndex={0}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => openInspectionRow(insp)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                openInspectionRow(insp);
                                                            }
                                                        }}
                                                    >
                                                        <td style={{ fontWeight: 600 }}>{clientName(insp)}</td>
                                                        <td>{insp.siteAddress || '—'}</td>
                                                        {canManage && <td>{insp.technicianId?.name || '—'}</td>}
                                                        <td>{formatFrequency(insp.frequency)}</td>
                                                        <td>{formatStandard(insp.standardCode)}</td>
                                                        <td style={{ color: 'var(--dash-muted)', whiteSpace: 'nowrap' }}>
                                                            {insp.createdAt ? new Date(insp.createdAt).toLocaleDateString() : '—'}
                                                        </td>
                                                        <td>{statusBadge(insp.status)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="list-cards-pane">
                                        {inspections.map((insp) => (
                                            <article
                                                key={`m-${insp._id}`}
                                                role="button"
                                                tabIndex={0}
                                                className="list-land-card list-land-card--clickable"
                                                onClick={() => openInspectionRow(insp)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openInspectionRow(insp);
                                                    }
                                                }}
                                            >
                                                <div className="list-land-field list-land-field--span">
                                                    <span className="list-land-label">Client</span>
                                                    <span className="list-land-value">{clientName(insp)}</span>
                                                </div>
                                                <div className="list-land-field list-land-field--span">
                                                    <span className="list-land-label">Site</span>
                                                    <span className="list-land-value">{insp.siteAddress || '—'}</span>
                                                </div>
                                                {canManage && (
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Technician</span>
                                                        <span className="list-land-value">{insp.technicianId?.name || '—'}</span>
                                                    </div>
                                                )}
                                                <div className="list-land-field">
                                                    <span className="list-land-label">Inspection Type</span>
                                                    <span className="list-land-value">{formatFrequency(insp.frequency)}</span>
                                                </div>
                                                <div className="list-land-field">
                                                    <span className="list-land-label">Standard</span>
                                                    <span className="list-land-value">{formatStandard(insp.standardCode)}</span>
                                                </div>
                                                <div className="list-land-field">
                                                    <span className="list-land-label">Date</span>
                                                    <span className="list-land-value list-land-value--muted">
                                                        {insp.createdAt ? new Date(insp.createdAt).toLocaleDateString() : '—'}
                                                    </span>
                                                </div>
                                                <div className="list-land-field list-land-field--span">
                                                    <span className="list-land-label">Status</span>
                                                    <span className="list-land-value">{statusBadge(insp.status)}</span>
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    <div style={{ marginTop: '32px', textAlign: 'center' }}>
                        <button
                            type="button"
                            className="dash-btn dash-btn-ghost"
                            onClick={() => navigate(canManage ? '/admin/dashboard' : '/technician/dashboard')}
                            style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}
                        >
                            ← Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
